import React from "react";
import { Button } from "@material-ui/core";
import { TextField } from "@material-ui/core";
import { Row, Col } from "react-flexbox-grid";

interface AddressFormProps {
  data: any;
  googleAddress?: any;
  onChange: (data: any) => void;
  onSubmit: () => void;
  onChangeLocation: () => void;
  onHide: () => void;
}

const Field: React.FC<{
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  inputProps?: any;
}> = ({
  label,
  required,
  value,
  onChange,
  placeholder = "Type here",
  disabled = false,
  inputProps,
}) => (
  <div style={{ marginBottom: 14 }}>
    <TextField
      style={{ width: "100%" }}
      label={
        <>
          {label}
          {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
        </>
      }
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      inputProps={inputProps}
    />
  </div>
);

const AddressForm: React.FC<AddressFormProps> = ({
  data,
  onChange,
  onSubmit,
  onChangeLocation,
  onHide,
  googleAddress,
}) => {
  const handleChange = (key: string, value: string) => {
    onChange({ ...data, [key]: value });
  };

  const addressTypes = [
    { value: "Home", label: "Home" },
    { value: "Work", label: "Work" },
    { value: "Other", label: "Other" },
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Address</h3>
        <div style={{ textAlign: "right" }}>
          <Button
            style={{
              color: "#ffffff",
              backgroundColor: "#ef7c01",
              marginRight: 8,
            }}
            onClick={onSubmit}
            disableRipple
            disableFocusRipple
          >
            Save
          </Button>
          <Button
            style={{
              color: "#ffffff",
              backgroundColor: "#c2c2c2",
            }}
            onClick={onHide}
            disableRipple
            disableFocusRipple
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* Location bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#f4f4f4",
          padding: "10px",
          border: "1px solid #D9D9D9",
          borderRadius: 12,
          marginTop: 16,
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ fontSize: 12, color: "#777", marginBottom: 6 }}>
            Location
          </div>
          <div
            style={{
              fontSize: 14,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data?.coordinates?.latitude &&
            data?.coordinates?.longitude &&
            data.coordinates.latitude !== 0 &&
            data.coordinates.longitude !== 0 ? (
              <>
                {googleAddress?.line1 ? `${googleAddress.line1}` : ""}
                {googleAddress?.line2 ? `, ${googleAddress.line2}` : ""}
                {googleAddress?.city ? `, ${googleAddress.city}` : ""}
                {googleAddress?.state ? `, ${googleAddress.state}` : ""}
                {googleAddress?.pincode ? ` - ${googleAddress.pincode}` : ""}
              </>
            ) : (
              <span style={{ color: "#666" }}>
                Click on change button to update geo location
              </span>
            )}
          </div>
        </div>
        <Button
          style={{
            color: "#ffffff",
            backgroundColor: "#08C8D3",
            marginLeft: 12,
          }}
          onClick={onChangeLocation}
          disableRipple
          disableFocusRipple
        >
          Change
        </Button>
      </div>

      {/* Form fields */}
      <Row>
        <Col xs={12} sm={6}>
          <Field
            label="Full name"
            required
            value={data?.name || ""}
            onChange={(v) => handleChange("name", v)}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Field
            label="Mobile number"
            required
            value={data?.phoneNo || ""}
            onChange={(v) => {
              const numeric = v.replace(/\D/g, "");
              if (numeric.length <= 10) {
                handleChange("phoneNo", numeric);
              }
            }}
            inputProps={{ maxLength: 10 }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Field
            label="House No. / Floor / Building"
            required
            value={data?.line1 || ""}
            onChange={(v) => handleChange("line1", v)}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Field
            label="Colony / Street / Area"
            required
            value={data?.line2 || ""}
            onChange={(v) => handleChange("line2", v)}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Field
            label="Landmark"
            value={data?.landmark || ""}
            onChange={(v) => handleChange("landmark", v)}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} sm={6}>
          <Field
            label="Pincode"
            required
            value={data?.pincode || ""}
            onChange={(v) => {
              const numeric = v.replace(/\D/g, "");
              if (numeric.length <= 6) {
                handleChange("pincode", numeric);
              }
            }}
            inputProps={{ maxLength: 6 }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Field
            label="City"
            required
            value={data?.city || ""}
            onChange={(v) => handleChange("city", v)}
            disabled={!!data?.city && !!googleAddress?.city}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} sm={6}>
          <Field
            label="District"
            required
            value={data?.district || ""}
            onChange={(v) => handleChange("district", v)}
            disabled={!!data?.district && !!googleAddress?.district}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Field
            label="State"
            required
            value={data?.state || ""}
            onChange={(v) => handleChange("state", v)}
            disabled={!!data?.state && !!googleAddress?.state}
          />
        </Col>
      </Row>

      {/* Address type */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ color: "#666", fontSize: 12, marginBottom: 6 }}>
          Address type
          <span style={{ color: "red", marginLeft: 2 }}>*</span>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
          {addressTypes.map((opt) => (
            <div
              key={opt.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                border: `1px solid ${
                  data?.type === opt.value ? "#6A5FF5" : "#D9D9D9"
                }`,
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 14,
                background: data?.type === opt.value ? "#F0EFFE" : "#fff",
                transition: "all 0.2s ease",
              }}
              onClick={() => handleChange("type", opt.value)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddressForm;
